import React, { createContext, useContext, useState, useCallback } from 'react';

type CommentContextType = {
    activeBlockId: string | null;
    showCommentCard: boolean;
    openCommentForBlock: (blockId: string) => void;
    closeComment: () => void;
    toggleCommentForBlock: (blockId: string) => void;
    setShowCommentCard: (value: boolean) => void;
    handleCommentClick: (blockId?: string) => void;
};

const CommentContext = createContext<CommentContextType | null>(null);

export const useComments = () => {
    const context = useContext(CommentContext);
    if (context === null) {
        throw new Error('useComments must be used within a CommentProvider');
    }
    return context;
};

export const CommentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [activeBlockId, setActiveBlockId] = useState<string | null>(null);
    const [isCommentCardOpen, setIsCommentCardOpen] = useState(false);

    const openCommentForBlock = useCallback((blockId: string) => {
        setActiveBlockId(blockId);
        setIsCommentCardOpen(true);
    }, []);

    const closeComment = useCallback(() => {
        setIsCommentCardOpen(false);
        setActiveBlockId(null);
    }, []);

    const toggleCommentForBlock = useCallback(
        (blockId: string) => {
            if (activeBlockId === blockId && isCommentCardOpen) {
                setIsCommentCardOpen(false);
                setActiveBlockId(null);
            } else {
                setActiveBlockId(blockId);
                setIsCommentCardOpen(true);
            }
        },
        [activeBlockId, isCommentCardOpen],
    );

    const handleCommentClick = useCallback(
        (blockId?: string) => {
            if (blockId) {
                toggleCommentForBlock(blockId);
            }
        },
        [toggleCommentForBlock],
    );

    const setShowCommentCard = useCallback((value: boolean) => {
        setIsCommentCardOpen(value);
        if (!value) {
            setActiveBlockId(null);
        }
    }, []);

    const value = {
        activeBlockId,
        showCommentCard: isCommentCardOpen && activeBlockId !== null,
        openCommentForBlock,
        closeComment,
        toggleCommentForBlock,
        setShowCommentCard,
        handleCommentClick,
    };

    React.useEffect(() => {
        const handleCommentOpenEvent = (event: Event) => {
            if (event instanceof CustomEvent) {
                const { blockId } = event.detail;
                if (blockId) {
                    openCommentForBlock(blockId);
                }
            }
        };

        document.addEventListener('saga:comment:open', handleCommentOpenEvent);
        return () => {
            document.removeEventListener('saga:comment:open', handleCommentOpenEvent);
        };
    }, [openCommentForBlock]);

    return <CommentContext.Provider value={value}>{children}</CommentContext.Provider>;
};

export const openCommentForBlock = (blockId: string) => {
    document.dispatchEvent(
        new CustomEvent('saga:comment:open', {
            detail: { blockId },
        }),
    );
};
